import Close from '@mui/icons-material/Close';
import {
    Dialog,
    DialogActions,
    DialogTitle,
    Button,
    DialogContent,
    ButtonProps,
    SxProps,
    IconButton,
    Stack,
    useTheme,
} from '@mui/material';
import i18n from 'i18n';
import React, {PropsWithChildren, useMemo} from 'react';
import {useDeviceInfo} from 'utils/hooks/useDeviceInfo';

export enum SideBarDialogType {
    common = 'common',
    light = 'light',
}

type SideBarDialogProps = {
    type?: SideBarDialogType;
    open: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    actions?: {
        cancel?: ButtonProps;
        submit?: ButtonProps;
        custom?: React.ReactNode;
    };
    customTitleActions?: React.ReactNode;
    customTitleBlock?: React.ReactNode;
    maxWidth?: string;
    height?: string;
    paddingBottom?: string;
    marginTop?: string;
    hideBackdrop?: boolean;
    top?: string;
    position?: 'left' | 'right';
    customStyle?: Record<string, any>;
} & PropsWithChildren;

export const SidebarDialog = ({
    open,
    onClose,
    title,
    children,
    actions,
    type = SideBarDialogType.common,
    maxWidth = '900px',
    customTitleActions,
    customTitleBlock,
    height = '100%',
    paddingBottom = '65px',
    marginTop,
    hideBackdrop = false,
    position = 'right',
    top,
    customStyle = {},
}: SideBarDialogProps) => {
    const theme = useTheme();
    const {isMobile} = useDeviceInfo();

    const style = useMemo(() => {
        const dialogStyle: SxProps = {
            '& .MuiDialog-container': {
                justifyContent: position === 'right' ? 'flex-end' : 'flex-start',
                '& > .MuiPaper-root': {
                    height,
                    margin: 0,
                    maxHeight: '100%',
                    borderRadius: 0,

                    marginTop: marginTop || isMobile ? 0 : '140px',
                    maxWidth,
                    width: '100%',
                    paddingBottom,
                    top,
                    backgroundColor: 'var(--mui-palette-background-default)',
                    '& .MuiDialogContent-root': {
                        backgroundColor: 'inherit',
                    },
                    '& .MuiTypography-root': {
                        backgroundColor: 'inherit',
                    },
                    '& .MuiDialogActions-root': {
                        backgroundColor: 'inherit',
                        padding: '16px',
                    },
                    [theme.breakpoints.down('sm')]: {
                        borderRadius: '16px 16px 0 0',
                        paddingBottom: 0,
                    },
                },
            },
            ...customStyle,
        };
        if (type === SideBarDialogType.light) {
            dialogStyle['& .MuiBackdrop-root'] = {
                backgroundColor: 'rgba(0,0,0,0)',
            };
        }
        return dialogStyle;
    }, [
        position,
        height,
        marginTop,
        maxWidth,
        paddingBottom,
        top,
        customStyle,
        type,
        isMobile,
        theme,
    ]);
    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={style}
            scroll='paper'
            hideBackdrop={hideBackdrop}
        >
            {(title || customTitleBlock) && (
                <Stack
                    sx={{
                        backgroundColor: 'inherit',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        [theme.breakpoints.down('sm')]: {alignItems: 'center'},
                    }}
                >
                    <DialogTitle
                        variant='h2'
                        sx={{
                            flexShrink: 1,
                            [theme.breakpoints.down('sm')]: {padding: '16px 0 16px 16px'},
                        }}
                    >
                        {customTitleBlock || (
                            <Stack
                                direction='row'
                                gap='16px'
                                alignItems='baseline'
                            >
                                {title || customTitleBlock}
                            </Stack>
                        )}
                    </DialogTitle>
                    {customTitleActions ? (
                        customTitleActions
                    ) : (
                        <IconButton
                            onClick={onClose}
                            sx={{marginRight: '16px'}}
                        >
                            <Close />
                        </IconButton>
                    )}
                </Stack>
            )}
            <DialogContent
                sx={{
                    [theme.breakpoints.down('sm')]: {padding: '8px 16px'},
                }}
            >
                {children}
            </DialogContent>
            {actions && (
                <DialogActions>
                    {actions?.cancel && (
                        <Button
                            variant='outlined'
                            children={<>{i18n.t('cancel', {ns: 'common'})}</>}
                            {...actions.cancel}
                        />
                    )}
                    {actions?.submit && (
                        <Button
                            children={<>{i18n.t('save', {ns: 'common'})}</>}
                            variant='contained'
                            color='primary'
                            {...actions.submit}
                        />
                    )}
                    {actions?.custom !== undefined && actions.custom}
                </DialogActions>
            )}
        </Dialog>
    );
};
